import { useServices } from "@/hooks/useServices";
import { BlockBlobClient } from "@azure/storage-blob";
import { inferMimeType, sanitizeMetadataValue } from "../utils/fileUtils";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import { logger } from "@/lib/logger";
import { restError } from "@/models/client/RestResponse";

/**
 * Matches API GetDocumentBlobUrlQueryHandler: `{uriBase}/{blobName}{sasToken}`.
 * Management API returns `sasToken` with a leading `?` (Azure `Uri.Query`); bare
 * tokens are supported with `?` vs `&` chosen from whether the path already has a query.
 */
const buildContainerBlobUrl = (
  uriBase: string,
  blobName: string,
  sasToken: string
): string => {
  const base = uriBase.endsWith("/") ? uriBase : `${uriBase}/`;
  const path = `${base}${blobName}`;
  if (sasToken.startsWith("?") || sasToken.startsWith("&")) {
    return `${path}${sasToken}`;
  }
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${sasToken}`;
};

const useAzureStorage = () => {
  const blobStorageService = useServices().items.blobStorage;

  const getWorkspaceStagingSasTokenUrl = async (
    blobName: string
  ): Promise<string | null> => {
    try {
      const tokenedUrl = await blobStorageService.getWorkspaceStagingSasToken();
      if (!tokenedUrl.success) {
        throw restError(
          tokenedUrl,
          "Failed to retrieve workspace staging SAS token."
        );
      }

      return buildContainerBlobUrl(
        tokenedUrl.value.uriBase,
        blobName,
        tokenedUrl.value.sasToken
      );
    } catch (error) {
      logger.error("Error getting workspace staging SAS token URL", {
        operation: "getWorkspaceStagingSasTokenUrl",
        error,
        blobName,
      });
      return null;
    }
  };

  const getSasTokenUrl = async (blobName: string): Promise<string | null> => {
    try {
      const tokenedUrl = await blobStorageService.getTokenedUrl();
      if (!tokenedUrl.success) {
        throw restError(tokenedUrl, "Failed to retrieve SAS token.");
      }

      return buildContainerBlobUrl(
        tokenedUrl.value.uriBase,
        blobName,
        tokenedUrl.value.sasToken
      );
    } catch (error) {
      logger.error("Failed to get a SAS URL for workspace storage", {
        operation: "getSasTokenUrl",
        error,
        blobName,
      });
      showErrorToast("Fejl ved hentning af upload URL");
      return null;
    }
  };

  const uploadFileToAzure = async (
    file: File,
    blobName: string,
    workspaceId: string
  ): Promise<void> => {
    const finalUri = await getSasTokenUrl(blobName);
    if (!finalUri) return;

    try {
      const client = new BlockBlobClient(finalUri);

      // Upload the file to Azure Blob Storage
      const uploadBlobResponse = await client.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
      });

      // Set metadata
      const metadataPutResponse = await client.setMetadata({
        name: sanitizeMetadataValue(file.name),
        type: sanitizeMetadataValue(file.type),
        workspaceid: sanitizeMetadataValue(workspaceId),
        size: sanitizeMetadataValue(file.size.toString()),
        mimetype: sanitizeMetadataValue(inferMimeType(file)),
      });

      if (!uploadBlobResponse || !metadataPutResponse) {
        logger.error(
          "Workspace storage returned no upload or metadata response",
          {
            operation: "uploadFileToAzure",
            blobName,
            workspaceId,
            hasUploadResponse: Boolean(uploadBlobResponse),
            hasMetadataResponse: Boolean(metadataPutResponse),
          }
        );
        showErrorToast("Fejl ved upload af fil til storage account");
        return;
      }
    } catch (error) {
      logger.error("Failed to upload file to workspace storage", {
        operation: "uploadFileToAzure",
        error,
        blobName,
        workspaceId,
      });
      showErrorToast("Upload til Azure mislykkedes");
      throw error;
    }
  };

  /**
   * Uploads a Microsoft Graph–converted PDF to the workspace-documents container
   * (same blob name as the DOCX entityId) so indexing can skip Gotenberg.
   */
  const uploadPdfToWorkspaceBlob = async (
    pdfBlob: Blob,
    blobName: string,
    workspaceId: string,
    sourceFileName: string
  ): Promise<boolean> => {
    const operation = "uploadPdfToWorkspaceBlob";
    const finalUri = await getWorkspaceStagingSasTokenUrl(blobName);
    if (!finalUri) {
      logger.warn("Could not upload workspace PDF — missing SAS URL", {
        operation,
        blobName,
        workspaceId,
      });
      return false;
    }

    try {
      const client = new BlockBlobClient(finalUri);
      await client.uploadData(pdfBlob, {
        blobHTTPHeaders: { blobContentType: "application/pdf" },
      });
      await client.setMetadata({
        name: sanitizeMetadataValue(sourceFileName),
        type: sanitizeMetadataValue("application/pdf"),
        workspaceid: sanitizeMetadataValue(workspaceId),
        size: sanitizeMetadataValue(pdfBlob.size.toString()),
        mimetype: sanitizeMetadataValue("application/pdf"),
        pdfsource: sanitizeMetadataValue("microsoft_graph"),
      });
      return true;
    } catch (error) {
      logger.error("Failed to upload workspace PDF blob", {
        operation,
        error,
        blobName,
        workspaceId,
      });
      return false;
    }
  };

  const createNewVersionOfFileInAzure = async (
    file: File,
    workspaceId: string
  ): Promise<string> => {
    try {
      const newBlobName = uuidv4();

      await uploadFileToAzure(file, newBlobName, workspaceId);
      return newBlobName;
    } catch (error) {
      showErrorToast("Kunne ikke oprette ny fil");
      throw error;
    }
  };

  const getBlobLastModified = async (
    blobName: string
  ): Promise<Date | null> => {
    const finalUri = await getSasTokenUrl(blobName);
    if (!finalUri) return null;

    try {
      const response = await fetch(finalUri, {
        method: "HEAD",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch metadata: ${response.status} ${response.statusText}`
        );
      }

      const lastModified = response.headers.get("Last-Modified");
      return lastModified ? new Date(lastModified) : null;
    } catch (error) {
      logger.error("Failed to read the blob's last-modified date", {
        operation: "getBlobLastModified",
        error,
        blobName,
      });
      showErrorToast("Fejl ved hentning af metadata");
      return null;
    }
  };

  const downloadFile = async (blobName: string): Promise<File | undefined> => {
    try {
      const finalUri = await getSasTokenUrl(blobName);
      const response = await fetch(finalUri!);
      if (!response.ok) {
        throw new Error(
          `Failed to download file: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      return new File([blob], blobName, { type: blob.type });
    } catch (error) {
      logger.error("Failed to download file from workspace storage", {
        operation: "downloadWorkspaceBlob",
        error,
        blobName,
      });
      showErrorToast("Download mislykkedes");
      return undefined;
    }
  };

  return {
    uploadFileToAzure,
    uploadPdfToWorkspaceBlob,
    createNewVersionOfFileInAzure,
    getBlobLastModified,
    downloadFile,
  };
};

export default useAzureStorage;
